import React from 'react';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { parsePhoneNumber } from 'libphonenumber-js';
import { Redirect, withRouter } from 'react-router-dom';
import { withTranslation, WithTranslation } from 'react-i18next';

import EclipseLoader from '../../components/EclipseLoader';
import Loader from '../../components/Loader';
import NoCookies from './NoCookies';
import NavBar, { View } from '../../components/NavBar';
import { Button } from '../../components/Button';
import { DatePicker } from '../../components/DatePicker';
import Footer from './Footer';
import ReceiptMessage from '../Payments/ReceiptPage/Message';

import * as api from '../../api';
import * as logger from '../../logger';
import * as store from '../../reducers/store';
import { applyTheme } from '../../utils';

import * as rootReducer from '../../reducers/root';
import { getMaybeAutocompletedDateOfBirth } from './utils';

import styles from './index.module.css';
import formStyles from '../FormStart/index.module.css';
import { PatientRouteProps } from '../router';
import { parseValue } from 'common/dist/utils/types';

enum Page {
  Login = 'LOGIN',
  Loading = 'LOADING',
  Expired = 'EXPIRED',
  Ineligible = 'INELIGIBLE',
  Success = 'SUCCESS',
}

interface Props extends DispatchProps, PatientRouteProps, WithTranslation {}
interface DispatchProps {
  setLoggedInPatient: (loggedInPatient: store.Patient) => void;
  setTheme: (theme: api.PracticeTheme.ThemeResponse) => void;
}

interface State {
  lastName: string;
  dateOfBirth?: Date;
  page: Page;
  isSubmitting: boolean;
  isLoading: boolean;
  practicePhoneNumber?: string;
}

const receiptStrings = {
  ineligible: `We were unable to find a receipt associated with your account. If you think this may be in error, please contact your practice`,
  expired:
    'Your receipt is no longer available to view. Please visit or call your clinic during normal business hours to obtain a copy of your receipt',
};

const MINIMUM_LOAD_TIME = 500; // ms

export class ReceiptAuth extends React.Component<Props, State> {
  private monthInput = React.createRef<HTMLInputElement>();
  private dayInput = React.createRef<HTMLInputElement>();
  private yearInput = React.createRef<HTMLInputElement>();

  state: State = {
    lastName: '',
    page: Page.Login,
    isSubmitting: false,
    isLoading: true,
  };

  async componentDidMount(): Promise<void> {
    const { authenticationCode } = this.props.match.params;

    try {
      const [isAuthenticated] = await Promise.all([
        api.client.Previsit.isAuthenticated(),
        this.getAndApplyLoginTheme(authenticationCode),
      ]);
      if (!isAuthenticated) {
        return;
      }
    } catch (err) {
      logger.error(
        err,
        `ReceiptAuth.componentDidMount ${this.props.t(
          'somethingWentWrongRefreshOrContactSupport',
        )}`,
      );
      alert(this.props.t('somethingWentWrongRefreshOrContactSupport'));
      return;
    } finally {
      this.setState({ isLoading: false });
    }

    try {
      // Make sure they're logged in as the correct user
      await api.client.Previsit.getReceiptInfo(authenticationCode);
    } catch (err) {
      // They're logged in as a different user, or their code changed
      logger.error(err, 'error fetching receipt');
      return;
    }

    return this.setState({ page: Page.Success });
  }

  async getAndApplyLoginTheme(authCode: string) {
    try {
      const theme = await api.client.Previsit.getThemeForLogin({
        domain: window.location.hostname,
        authCode,
      });
      this.props.setTheme(theme);
      applyTheme(theme);
      this.setState({ practicePhoneNumber: theme.phoneNumber });
    } catch (e) {
      return;
    }
  }

  handleSubmit = async (evt: React.FormEvent<HTMLFormElement>) => {
    const { dateOfBirth, lastName } = this.state;
    const { authenticationCode } = this.props.match.params;
    evt.preventDefault();
    evt.stopPropagation();

    const payloadDob =
      dateOfBirth ||
      getMaybeAutocompletedDateOfBirth({
        month: this.monthInput.current?.value,
        day: this.dayInput.current?.value,
        year: this.yearInput.current?.value,
      });

    if (!payloadDob) {
      alert('Please enter your date of birth');
      return;
    }

    if (!lastName) {
      alert('Please enter your last name');
      return;
    }

    this.setState({
      isSubmitting: true,
      page: Page.Loading,
    });

    const start = new Date();

    try {
      const { patient } = await api.client.Previsit.receiptLogin(
        authenticationCode,
        lastName,
        payloadDob,
      );
      if (patient) {
        this.props.setLoggedInPatient(patient);
      }
    } catch (err) {
      const { status } = JSON.parse(err.message);
      const timeElapsed = new Date().getTime() - start.getTime();
      if (timeElapsed <= MINIMUM_LOAD_TIME) {
        await new Promise((res) =>
          setTimeout(res, MINIMUM_LOAD_TIME - timeElapsed),
        );
      }

      if (status >= 500) {
        alert(this.props.t('somethingWentWrongRefreshOrContactSupport'));
        return;
      }

      const loginError = parseValue<{ message: string }>(err.message);
      if (loginError?.message === 'EXPIRED') {
        this.setState({
          isSubmitting: false,
          page: Page.Expired,
        });
        return;
      }
      if (loginError?.message === 'PAYMENT_NOT_FOUND') {
        this.setState({
          isSubmitting: false,
          page: Page.Ineligible,
        });
        return;
      }
      alert(this.props.t('enterPatientDateOfBirthAndLastName'));

      this.setState({ isSubmitting: false });
      return;
    }

    const timeElapsed = new Date().getTime() - start.getTime();
    if (timeElapsed <= MINIMUM_LOAD_TIME) {
      await new Promise((res) =>
        setTimeout(res, MINIMUM_LOAD_TIME - timeElapsed),
      );
    }
    this.setState({
      isSubmitting: false,
      page: Page.Success,
    });
  };

  getReceiptString(type: 'expired' | 'ineligible'): string {
    let phoneNumber: string | undefined;
    try {
      phoneNumber = parsePhoneNumber(
        this.state.practicePhoneNumber ?? '',
        'US',
      ).formatNational();
    } catch {
      phoneNumber = '';
    }
    const suffix = phoneNumber ? ` at ${phoneNumber}.` : '.';
    return receiptStrings[type] + suffix;
  }

  render() {
    const { dateOfBirth, isLoading, isSubmitting, lastName, page } = this.state;
    const { authenticationCode } = this.props.match.params;
    if (isLoading) return <Loader />;

    if (page === Page.Loading) {
      return (
        <div className={formStyles.Form}>
          <NavBar view={View.Login} />
          <EclipseLoader message="Finding your receipt..." />
        </div>
      );
    }

    if (page === Page.Expired) {
      return <ReceiptMessage message={this.getReceiptString('expired')} />;
    }

    if (page === Page.Ineligible) {
      return <ReceiptMessage message={this.getReceiptString('ineligible')} />;
    }

    if (page === Page.Success) {
      return <Redirect to={`/receipt/${authenticationCode}`} />;
    }

    const readyForLogin = dateOfBirth && lastName;
    const message = `Please enter the following information to access your payment details`;

    return (
      <div className={styles.LoginWrapper}>
        <NavBar view={View.Login} />
        <div className={styles.Login}>
          <NoCookies />
          <div className={styles.Instructions}>{message}</div>
          <form onSubmit={this.handleSubmit}>
            <div
              className={classNames(styles.Inputs, styles.RegistrationInputs)}
            >
              <DatePicker
                isLogin={true}
                isRegistration={true}
                onChange={(dateOfBirth) => this.setState({ dateOfBirth })}
                placeholder={this.props.t('patientDateOfBirth')}
                monthInput={this.monthInput}
                dayInput={this.dayInput}
                yearInput={this.yearInput}
              />
              <input
                id="patient-last-name"
                className={styles.Input}
                type="text"
                value={lastName}
                placeholder={this.props.t('patientLastName')}
                onChange={(e) =>
                  this.setState({ lastName: e.currentTarget.value })
                }
              />
            </div>
            <Button
              id="sign-in-button"
              children="Continue"
              type="submit"
              disabled={!readyForLogin || isSubmitting}
            />
          </form>
        </div>

        <Footer />
      </div>
    );
  }
}

function mapDispatchToProps(dispatch: Function): DispatchProps {
  return {
    setTheme: (theme: api.PracticeTheme.ThemeResponse) =>
      dispatch(rootReducer.setTheme(theme)),
    setLoggedInPatient: (patient: store.Patient) =>
      dispatch(rootReducer.setLoggedInPatient(patient)),
  };
}

export default withTranslation(['login'])(
  connect(undefined, mapDispatchToProps)(withRouter(ReceiptAuth)),
);
